import React from 'react';
import { RecordDetail } from '../activity/record-detail';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { routes, wizardRoutes } from '../../constants/page-routes/page-routes';
import { useAuth } from '../../context/auth-provider';
import useVisitorLogHandler from '../hooks/use-visitor-log-handler';
import useDailyLogHandler from '../hooks/use-daily-log-handler';
import {
  dailyLogPaths,
  generatePaths,
  visitorLogPaths,
} from './path-generator';
import { getNextAndPrevPaths } from './next-and-prev-paths';
import { getNavigationFromVisitorType } from '../layout/visitor-log/navbar-controller';
import { User } from '../../interfaces/user-interfaces';
import useBuildingData from '../hooks/use-get-building-data';
import {
  RouteTransition,
  isDeskPath,
} from '../route-transition/route-transition';
import { DeskShell } from '../layout/desk-shell';
import { Presence } from '../../motion/presence';
import { WizardLayout } from '../layout/wizard-layout';
import { OnboardingPage } from '../onboarding/onboarding-page';
import {
  RequireAuth,
  RequireRole,
  RequirePermission,
  RequireDesk,
} from '../auth/require-role';
import { isDeskScreen } from '../../utils/products';
import { ROLES } from '../../auth/roles';
import { PERMISSIONS } from '../../auth/permissions';
import { BrandedLoader } from '../loader/branded-loader';
import { LoadError } from '../pages/dashboard/dashboard-shared-components/load-state';
import { NotFound } from '../pages/not-found/not-found';
import styled from '@emotion/styled';
import { spacing } from '../../style-utils/theme';

/* The failure gets the page to itself: there is no shell to sit inside,
   because the shell is the thing that could not be built. */
const DeskLoadFailure = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: ${spacing.xl} ${spacing.md};
`;

// Role gates for the standalone (non-wizard) routes. '/' (login) stays unguarded;
// RequireRole redirects a wrong-role user to their own home.
const ROUTE_ROLES: Record<string, string[]> = {
  '/main-menu': [ROLES.OWNER, ROLES.PROPERTY_MANAGER, ROLES.CONCIERGE],
  // '/view-report' MOVED TO ROUTE_PERMISSIONS. It was [OWNER, PROPERTY_MANAGER]
  // — stricter than anything the server enforces — while the "View Daily
  // Report" tile on the main menu carries no gate at all. So a concierge saw
  // the tile, pressed it, and RequireRole bounced them straight back to
  // /main-menu with no message. That is the whole of "view daily report just
  // does not work": the request never left the browser, and the prod log for
  // the session shows no GET /logs at all.
  '/roster': [ROLES.OWNER, ROLES.PROPERTY_MANAGER, ROLES.CONCIERGE],
  // The parcel desk is front-desk work, so the concierge is first-class here.
  '/parcels': [ROLES.OWNER, ROLES.PROPERTY_MANAGER, ROLES.CONCIERGE],
  '/iq-handover': [ROLES.OWNER, ROLES.PROPERTY_MANAGER, ROLES.CONCIERGE],
  // Independent Stays IS5: a co-host reaches /dashboard for Stays only — their
  // shell is Stays-only (utils/products.ts deskInPlan), so every Desk path in it
  // sends them to their listings.
  '/dashboard/*': [ROLES.OWNER, ROLES.PROPERTY_MANAGER, ROLES.STAYS_COHOST],
};

// Permission gates, for routes whose audience is not a job title.
//
// The resident portal moved out of ROUTE_ROLES with migration 037: a resident
// holds no role to gate on, and an owner who also lives here holds one that
// would fail a resident test. `portal:read` answers both — the server grants it
// from an open occupancy, so the gate opens for anyone who actually lives
// somewhere and closes on the next navigation after they move out.
//
// ONE KEY FOR EVERY PORTAL PATH. This map is looked up by the route ENTRY's
// path, exactly; the portal is one entry ('/portal/*') whose tabs are nested
// routes inside its shell, so this key gates /portal/visitors and
// /portal/parcels too. '/tenant-dashboard' needs no key of its own: it only
// redirects here, and the gate applies on arrival.
const ROUTE_PERMISSIONS: Record<string, string> = {
  '/portal/*': PERMISSIONS.PORTAL_READ,
  // The daily report. `report:read`, which the concierge holds
  // (lobbero_be/src/authz/permissions.js — CONCIERGE carries REPORT_READ and
  // REPORT_EXPORT), and which is what the endpoints behind this page check:
  // GET /logs wants visitorlog:read + dailylog:read, POST /daily-report wants
  // report:export. A concierge holds all four. Mirroring the capability instead
  // of naming two job titles is what stops the gate drifting from the server's
  // rule the next time the matrix changes — which is exactly what had happened.
  '/view-report': PERMISSIONS.REPORT_READ,
  // The shift monitor mirrors its own backend gate exactly
  // (lobbero_be/src/CRUD/shifts.js:371) rather than picking a role list that
  // happens to line up today. It is `user:read`, NOT `shift:clock`: the desk
  // clocks in, but the second group this view renders reports that a named
  // person is logged in and has NOT clocked in, which is supervision. A
  // concierge holds shift:clock and must still not reach this page.
  '/shift-monitor': PERMISSIONS.USER_READ,
  // "Coming out". A permission gate, not a role gate, and deliberately the SAME
  // permission the endpoint it calls is gated on
  // (lobbero_be/src/CRUD/visitor-log.js — POST /visitor-log/:id/exit requires
  // visitorlog:create). Recording that somebody left is the same class of act
  // as recording that they arrived; a role list would drift from the server's
  // rule the first time the matrix changes.
  '/visitor-exit': PERMISSIONS.VISITORLOG_CREATE,
  // "Activities out", same rule: POST /daily-log/:id/close is gated on
  // dailylog:create server-side, so the route is too.
  '/daily-log-exit': PERMISSIONS.DAILYLOG_CREATE,
  // "Expected today" (resident-portal Plan 2, RP-2.11). READ, not create: the
  // list is what the route shows, and GET /buildings/:b/expected is gated on
  // visitorpass:read. The one-tap sign-in on it is gated server-side on
  // visitorlog:create, and the screen explains itself when that is missing.
  '/visitor-log/expected': PERMISSIONS.VISITORPASS_READ,
  // The desk's pass register (owner decision 2026-09-29): read-only by route,
  // row actions as the server allows per pass.
  '/visitor-log/passes': PERMISSIONS.VISITORPASS_READ,
  // A hotel's reception (Independent Stays IS9): the lists are stays:desk, as
  // GET /api/stays/reception/* is; the writes need stays:reception and the
  // page says so when it is missing.
  '/reception': PERMISSIONS.STAYS_DESK,
};
const DESK_ROLES = [ROLES.OWNER, ROLES.PROPERTY_MANAGER, ROLES.CONCIERGE];

export const AppRouter = () => {
  const location = useLocation();
  const currentURLPath = location.pathname;

  const {
    user,
  }: {
    user: User | null | undefined;
  } = useAuth();

  const { id } = user || {};
  const needsBuildingContext =
    currentURLPath === '/main-menu' ||
    currentURLPath.startsWith('/visitor-log') ||
    currentURLPath.startsWith('/daily-log') ||
    currentURLPath === '/view-report' ||
    currentURLPath === '/roster' ||
    // The parcel shelf is per-building (its RLS policy is building-narrowed),
    // and the resident picker needs a building to load a roster from.
    currentURLPath === '/parcels' ||
    // The exit desk reads ONE building's open arrivals (/buildings/:id/presence)
    // and writes a departure against a row inside it. Without this line
    // selectedBuilding is undefined, the list can only render empty, and the
    // redirect below never fires because the path is not considered
    // building-bound at all.
    currentURLPath === '/visitor-exit' ||
    currentURLPath === '/iq-handover';
  // '/shift-monitor' IS DELIBERATELY ABSENT from that list now. It used to read
  // one building's shift state and needed the shell to have chosen a building;
  // it is a redirect into /dashboard/shifts since the Shifts tab absorbed it,
  // and the tab owns its own building picker. Leaving it in would have been
  // actively harmful rather than merely redundant: `requiresSelectedBuilding`
  // sends a user with no selected building to /main-menu BEFORE the route's
  // element mounts, so an old bookmark would bounce to the main menu instead of
  // reaching the page it moved to.
  const {
    selectedBuilding,
    buildingData: buildings,
    loading: buildingsLoading,
    buildingsError,
    selectBuildingById,
  } = useBuildingData(user, {
    enabled: needsBuildingContext,
    loadAnalytics: false,
  });
  const selectedBuildingNumber = Number(selectedBuilding?.id);
  const activePropertyIds =
    !buildingsLoading && Number.isInteger(selectedBuildingNumber)
      ? [selectedBuildingNumber]
      : [];
  const {
    visitorLogState,
    visitorFinish,
    visitorChangeHandler,
    addNewName,
    setCapture: setVisitorCapture,
  } = useVisitorLogHandler(id, activePropertyIds);

  const {
    dailyLogChangeHandler,
    dailyLogFinish,
    dailyLogState,
    setCapture: setDailyCapture,
  } = useDailyLogHandler(id, activePropertyIds);

  const visitorLogRoutes = generatePaths(visitorLogPaths);

  const dailyLogRoutes = generatePaths(dailyLogPaths);

  const navigate = useNavigate();

  const { cookies } = useAuth();

  const allPaths = [...visitorLogRoutes, ...dailyLogRoutes];

  // (Role redirection is now handled declaratively by RequireRole on each route,
  // replacing the old employee-only, deps-[user], substring-matching effect.)

  // Shell-level values for the persistent wizard layout, derived from the URL
  // (only the active route renders, so the current path is the source of truth).
  const isVisitorPath = currentURLPath.includes('/visitor-log');
  const layoutState = isVisitorPath ? visitorLogState : dailyLogState;
  const layoutNavigation = getNavigationFromVisitorType(
    isVisitorPath ? visitorLogState.visitorType : dailyLogState.buildingActivity
  );
  const layoutSelectedOption = isVisitorPath ? '/visitor-log' : '/daily-log';
  const requiresSelectedBuilding =
    needsBuildingContext && currentURLPath !== '/main-menu';

  const deskLoading = requiresSelectedBuilding && buildingsLoading;

  const renderRoute = (route: (typeof routes)[number]) => {
          const { path, component: Component, options = [] } = route;

          const { nextPath, prevPath, lastPath } = getNextAndPrevPaths(
            currentURLPath,
            allPaths
          );

          const state = path.includes('/visitor-log')
            ? visitorLogState
            : dailyLogState;

          const changeHandler = path.includes('/visitor-log')
            ? visitorChangeHandler
            : dailyLogChangeHandler;

          const finish = path.includes('/visitor-log')
            ? visitorFinish
            : dailyLogFinish;

          // The capture fields (unit_id, the resolution columns,
          // field_provenance) are a number, a nullable uuid and an object, so
          // they bypass the string-only changeHandler. See component-props.
          const setCapture = path.includes('/visitor-log')
            ? setVisitorCapture
            : setDailyCapture;

          const navigation = getNavigationFromVisitorType(
            path.includes('/visitor-log')
              ? visitorLogState.visitorType
              : dailyLogState.buildingActivity
          );

          const selectedOption = path.includes('/visitor-log')
            ? '/visitor-log'
            : '/daily-log';

          const roles = ROUTE_ROLES[path];
          const permission = ROUTE_PERMISSIONS[path];
          const element = (
            <Component
              selectedBuilding={selectedBuilding}
              addNewName={addNewName}
              selectedOption={selectedOption}
              lastPath={lastPath}
              navigation={navigation}
              allPaths={allPaths}
              nextPath={nextPath}
              prevPath={prevPath}
              options={options}
              cookies={cookies}
              state={state}
              navigate={navigate}
              path={path}
              changeHandler={changeHandler}
              setCapture={setCapture}
              finish={finish}
              buildings={buildings}
              buildingsLoading={buildingsLoading}
              onSelectBuilding={selectBuildingById}
            />
          );

          // eslint-disable-next-line no-nested-ternary
          const guarded = permission ? (
            <RequirePermission permission={permission}>
              {element}
            </RequirePermission>
          ) : roles ? (
            <RequireRole roles={roles}>{element}</RequireRole>
          ) : (
            element
          );

          return (
            <Route
              key={path}
              path={path}
              element={
                // The front-desk screens also need Lobbero Desk as a product
                // (Independent Stays IS1): a Stays-only owner is still an owner.
                isDeskScreen(path) ? <RequireDesk>{guarded}</RequireDesk> : guarded
              }
            />
          );
  };

  /* The one rail for every desk screen. `selectedOption` lights the rail's
     destination only on the two log sections; the parcel shelf and the exit
     desks light nothing, because they are not either of those. */
  const deskSidebar = {
    navigate,
    path: currentURLPath,
    selectedOption: currentURLPath.startsWith('/visitor-log')
      ? '/visitor-log'
      : currentURLPath.startsWith('/daily-log')
        ? '/daily-log'
        : '',
    selectedBuilding,
    buildings,
    buildingsLoading,
    onSelectBuilding: selectBuildingById,
  };

  let body: React.ReactNode = null;

  if (deskLoading) {
    // Nothing beneath the loader: every desk screen records against a
    // building, so there is nothing to show until the list has answered. The
    // loader itself lives in the <Presence> below so it fades out over the
    // screen that mounts the moment this flips.
    body = null;
  /* A FAILED LOAD IS NOT AN EMPTY ONE, and must not be answered by a redirect.
     Bouncing to /main-menu here sent the user to a menu whose every tile is
     disabled, with nothing on either screen saying a request had failed — the
     desk's conclusion is "the app is broken today", and the only recovery
     anybody finds is a reload. Say what happened and offer the retry. */
  } else if (requiresSelectedBuilding && buildingsError) {
    body = (
      <DeskLoadFailure>
        <LoadError message={buildingsError} onRetry={() => navigate(0)} />
      </DeskLoadFailure>
    );
  /* NO BUILDING CHOSEN YET, AND THE PAGE IS ABOUT ONE BUILDING.
     The redirect is right — every desk screen records against a building, so
     there is nothing to show until one is picked. What was wrong is that it
     happened in silence: an owner covering two buildings who opened a bookmark
     to /parcels, or pressed Back into /view-report, landed on the main menu with
     no account of why, which reads as the app having lost the page. The
     destination travels with them so the menu can name it and return them to it
     the moment they choose. The query string travels too: a cold deep link
     like /visitor-log/expected?pass=<id> used to arrive without the pass. */
  } else if (requiresSelectedBuilding && !selectedBuilding) {
    body = (
      <Navigate
        to="/main-menu"
        replace
        state={{ deskFrom: currentURLPath, deskSearch: location.search }}
      />
    );
  } else {
    body = (
    <RouteTransition>
      {(location) => (
      <Routes location={location}>
        {/* Authenticated first-run wizard (any role). Rendered here (not in the
            public router) so it sits behind auth; the container itself routes a
            completed user onward. */}
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <OnboardingPage />
            </RequireAuth>
          }
        />

        {/* Screens with their own furniture: sign-in, the dashboard, the
            account page, the resident portal, the IQ handover page. */}
        {routes.filter((route) => !isDeskPath(route.path)).map(renderRoute)}

        {/* THE DESK. One shell — rail, building panel, <main> — mounted once
            for every desk screen, so moving between them keeps the furniture
            and transitions only the content (layout/desk-shell.tsx). The
            wizard's own step shell nests inside it. */}
        <Route
          element={
            <RequireRole roles={DESK_ROLES}>
              <DeskShell sidebar={deskSidebar} />
            </RequireRole>
          }
        >
          {routes.filter((route) => isDeskPath(route.path)).map(renderRoute)}

        {/* Wizard steps share ONE persistent shell: <WizardLayout> stays mounted
            across steps and only the step pane inside its <Outlet /> swaps, so
            Next/Back animates smoothly instead of rebuilding the whole page. */}
        <Route
          element={
            <RequireRole roles={DESK_ROLES}>
              <WizardLayout
                navigate={navigate}
                navigation={layoutNavigation}
                state={layoutState}
                selectedOption={layoutSelectedOption}
                selectedBuilding={selectedBuilding}
              />
            </RequireRole>
          }
        >
          {wizardRoutes.map(route => {
            const {
              path,
              component: Component,
              options = [],
              noWizardContext = false,
            } = route;

            const { nextPath, prevPath, lastPath } = getNextAndPrevPaths(
              currentURLPath,
              allPaths
            );

            const isVisitor = path.includes('/visitor-log');
            const state = isVisitor ? visitorLogState : dailyLogState;
            const changeHandler = isVisitor
              ? visitorChangeHandler
              : dailyLogChangeHandler;
            const finish = isVisitor ? visitorFinish : dailyLogFinish;
            const setCapture = isVisitor ? setVisitorCapture : setDailyCapture;
            const navigation = getNavigationFromVisitorType(
              isVisitor
                ? visitorLogState.visitorType
                : dailyLogState.buildingActivity
            );
            const selectedOption = isVisitor ? '/visitor-log' : '/daily-log';
            const entryPath = isVisitor ? '/visitor-log' : '/daily-log';
            // A ROUTE THAT NEEDS NO TYPE CANNOT BE MISSING ONE.
            //
            // The guard below exists for deep links into step 4 of a flow whose
            // step 1 was never answered. `noWizardContext` marks the routes that
            // are one screen rather than a step — they are reached WITHOUT a
            // type by design, so measuring them against "has a type been picked"
            // asks a question they never had an answer to, and the redirect made
            // Resident in / out unreachable on every attempt.
            const hasWizardContext =
              noWizardContext ||
              (isVisitor
                ? Boolean(visitorLogState.visitorType)
                : Boolean(dailyLogState.buildingActivity));
            const stepElement =
              path !== entryPath && !hasWizardContext ? (
                <Navigate to={entryPath} replace />
              ) : (
                <Component
                  selectedBuilding={selectedBuilding}
                  addNewName={addNewName}
                  selectedOption={selectedOption}
                  lastPath={lastPath}
                  navigation={navigation}
                  allPaths={allPaths}
                  nextPath={nextPath}
                  prevPath={prevPath}
                  options={options}
                  cookies={cookies}
                  state={state}
                  navigate={navigate}
                  path={path}
                  changeHandler={changeHandler}
                  setCapture={setCapture}
                  finish={finish}
                  buildings={buildings}
                  buildingsLoading={buildingsLoading}
                  onSelectBuilding={selectBuildingById}
                />
              );

            // The visitor-log and daily-log wizards are front-desk screens too:
            // the same product gate as renderRoute's (Independent Stays IS1;
            // found by J-IS3.6 — a typed /visitor-log opened the wizard for a
            // Stays-only owner).
            return (
              <Route
                key={path}
                path={path}
                element={isDeskScreen(path) ? <RequireDesk>{stepElement}</RequireDesk> : stepElement}
              />
            );
          })}
        </Route>
        </Route>

        {/* ONE RECORD, BY DIRECT LINK (brief §13 and §20).
            A real route rather than a modal, because the requirement is that the
            link survive a refresh and be shareable — a modal over a list does
            neither. `dailylog:read` mirrors the endpoint's own gate
            (lobbero_be/src/CRUD/activity.js) rather than a role list that
            happens to line up today, and the endpoint answers 404 for a record
            outside the caller's scope, so this page cannot be used to discover
            which ids exist elsewhere. */}
        <Route
          path="/record/:source/:id"
          element={
            <RequirePermission permission={PERMISSIONS.DAILYLOG_READ}>
              <RecordDetail />
            </RequirePermission>
          }
        />

        {/* NOTHING MATCHED.
            Position in this list is irrelevant — react-router v6 ranks routes
            by specificity rather than by source order, so `*` is only ever the
            last resort and cannot shadow the wizard routes declared below it.
            RENDERED, NOT REDIRECTED: a silent bounce home is what the rest of
            this shell used to do, and it leaves the reader thinking they pressed
            the wrong thing rather than knowing the address does not exist. */}
        <Route path="*" element={<NotFound />} />

      </Routes>
      )}
    </RouteTransition>
    );
  }

  return (
    <>
      <Presence>
        {deskLoading ? (
          <BrandedLoader key="desk" label="Preparing the building desk…" />
        ) : null}
      </Presence>
      {body}
    </>
  );
};
